import { useQuery } from '@apollo/client/react';
import { Target } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SearchResult, useCommandPalette } from '../hooks/useCommandPalette';
import { RecentItem, useRecentItems } from '../hooks/useRecentItems';

import { useEntityActions } from './command-palette/EntityActions';
import { ItemPreview } from './command-palette/ItemPreview';
import { RecentItems } from './command-palette/RecentItems';
import { SearchResults } from './command-palette/SearchResults';
import {
  Command,
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from './ui/command';

import { NewDealDialog } from '@/pages/investments/direct/NewDealDialog';
import {
  GET_ACCOUNTS,
  GET_EMPLOYEES_AND_DEAL_SELECTS
} from '@/pages/investments/direct/queries';
import type {
  EmployeesAndDealSelects,
  GetAccounts
} from '@/pages/investments/direct/types';

interface GlobalCommandPaletteProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function GlobalCommandPalette({
  onOpenChange,
  open
}: GlobalCommandPaletteProps) {
  const {
    clearSearch,
    hasResults,
    isEmpty,
    loading,
    results,
    searchTerm,
    setSearchTerm
  } = useCommandPalette();

  const { addRecentItem, getRecentItems } = useRecentItems();
  const { emailContact, viewEntity } = useEntityActions();
  const [selectedItem, setSelectedItem] = useState<
    null | RecentItem | SearchResult
  >(null);
  const [commandValue, setCommandValue] = useState<string>('');
  const [isCreateDealOpen, setIsCreateDealOpen] = useState(false);

  const {
    data: dealSelectsData,
    error: dealSelectsError,
    loading: dealSelectsLoading
  } = useQuery<EmployeesAndDealSelects>(GET_EMPLOYEES_AND_DEAL_SELECTS, {
    skip: !open
  });

  const {
    data: dealAccountsData,
    error: dealAccountsError,
    loading: dealAccountsLoading
  } = useQuery<GetAccounts>(GET_ACCOUNTS, {
    skip: !open,
    variables: { name: [] }
  });

  const isDealActionReady = Boolean(
    !dealSelectsLoading &&
      !dealAccountsLoading &&
      !dealSelectsError &&
      !dealAccountsError &&
      dealSelectsData &&
      dealAccountsData
  );

  // Clear search and selection when dialog closes
  useEffect(() => {
    if (!open) {
      clearSearch();
      setSelectedItem(null);
      setCommandValue('');
    }
  }, [open, clearSearch]);

  // Reset command selection when search term changes
  useEffect(() => {
    setCommandValue('');
  }, [searchTerm]);

  const handleHover = useCallback((entity: RecentItem | SearchResult) => {
    setSelectedItem(entity);
  }, []);

  // Handle keyboard navigation - update preview when value changes
  useEffect(() => {
    if (!commandValue) return;

    // Parse the command value to find the item
    // Format: "type-id-name" or "type-id"
    const match = /^(account|contact|opportunity|view|action)-([^-]+)/.exec(
      commandValue
    );
    if (!match) return;

    const [, type, id] = match;
    if (type === 'action') return;

    // Find the item in results or recent items
    let foundItem: null | RecentItem | SearchResult = null;

    if (isEmpty) {
      // Search in recent items
      const recentItems = getRecentItems();
      foundItem =
        recentItems.find(item => item.type === type && item.id === id) || null;
    } else if (searchTerm.length >= 2) {
      // Search in results
      foundItem =
        results.all.find(item => item.type === type && item.id === id) || null;
    }

    if (foundItem) {
      setSelectedItem(foundItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commandValue, results.all, isEmpty, searchTerm]);

  const handleSelect = useCallback(
    (entity: RecentItem | SearchResult) => {
      // Add to recent items
      addRecentItem({
        id: entity.id,
        metadata: entity.metadata,
        name: entity.name,
        type: entity.type
      });

      // Navigate to entity
      viewEntity(entity);

      // Close dialog
      onOpenChange(false);
    },
    [addRecentItem, viewEntity, onOpenChange]
  );

  const actions = useMemo(
    () => [
      {
        disabled: !isDealActionReady,
        icon: Target,
        id: 'create-deal',
        keywords: ['deal', 'opportunity', 'create', 'new', 'investment'],
        label: 'Create Investment Opportunity',
        onSelect: () => {
          if (!isDealActionReady) {
            toast.info('Loading deal setup data...');
            return;
          }
          onOpenChange(false);
          setIsCreateDealOpen(true);
        }
      }
    ],
    [isDealActionReady, onOpenChange]
  );

  const actionMatches = (query: string) => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return actions;
    return actions.filter(
      action =>
        action.label.toLowerCase().includes(trimmed) ||
        action.keywords.some(keyword => keyword.includes(trimmed))
    );
  };

  const handleAction = useCallback(
    (action: string) => {
      if (!selectedItem) return;

      // Add to recent items
      addRecentItem({
        id: selectedItem.id,
        metadata: selectedItem.metadata,
        name: selectedItem.name,
        type: selectedItem.type
      });

      switch (action) {
        case 'copy-email':
          if (selectedItem.metadata?.email) {
            navigator.clipboard.writeText(selectedItem.metadata.email);
            toast.success('Email copied to clipboard');
          }
          break;
        case 'email':
          if (selectedItem.type === 'contact' && selectedItem.metadata?.email) {
            emailContact(selectedItem);
          }
          break;
        case 'open':
          viewEntity(selectedItem);
          onOpenChange(false);
          break;
        case 'open-new-tab':
          window.open(getEntityPath(selectedItem), '_blank');
          onOpenChange(false);
          break;
      }
    },
    [selectedItem, addRecentItem, viewEntity, emailContact, onOpenChange]
  );

  const getEntityPath = (entity: RecentItem | SearchResult): string => {
    switch (entity.type) {
      case 'account':
        return `/firm/crm-accounts/${entity.id}`;
      case 'contact':
        return `/firm/crm-contacts/${entity.id}`;
      case 'opportunity':
        return `/investments/direct/${entity.id}/overview`;
      case 'view':
        if (entity.metadata?.targetModel === 'account') {
          return `/firm/crm-accounts?viewId=${entity.id}`;
        } else {
          return `/firm/crm-contacts?viewId=${entity.id}`;
        }
      default:
        return '/';
    }
  };

  // Handle keyboard shortcuts for row actions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when dialog is open and item is selected
      if (!open || !selectedItem) return;

      // Ctrl/Cmd + Shift + Enter - Open in new tab
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleAction('open-new-tab');
        return;
      }

      // Ctrl/Cmd + E - Send email (for contacts)
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === 'e' &&
        selectedItem.type === 'contact' &&
        selectedItem.metadata?.email
      ) {
        e.preventDefault();
        e.stopPropagation();
        handleAction('email');
        return;
      }

      // Ctrl/Cmd + C - Copy email
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === 'c' &&
        selectedItem.metadata?.email
      ) {
        e.preventDefault();
        e.stopPropagation();
        handleAction('copy-email');
        return;
      }
    };

    // Use capture phase to intercept before Command component
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [open, selectedItem, handleAction]);

  const matchedActions = actionMatches(searchTerm);

  return (
    <>
      <CommandDialog
        onOpenChange={onOpenChange}
        open={open}
      >
        <div className="flex h-full min-h-0">
          {/* Left panel - Search */}
          <div className="flex min-h-0 min-w-0 flex-[2] flex-col border-r">
            <Command
              className="flex min-h-0 flex-1 flex-col"
              onValueChange={setCommandValue}
              shouldFilter={false}
              value={commandValue}
            >
              <CommandInput
                onValueChange={setSearchTerm}
                placeholder="Search investment opportunities..."
                value={searchTerm}
              />
              <CommandList className="max-h-[9999px] min-h-0 flex-1 overflow-y-auto">
                {matchedActions.length > 0 && (
                  <CommandGroup heading="Actions">
                    {matchedActions.map(action => {
                      const Icon = action.icon;
                      return (
                        <CommandItem
                          className="flex items-center gap-3 px-3 py-2"
                          disabled={'disabled' in action && action.disabled}
                          key={action.id}
                          onSelect={action.onSelect}
                          value={`action-${action.id}-${action.label}`}
                        >
                          <Icon className="text-muted-foreground h-4 w-4" />
                          <span className="font-medium">{action.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}

                {isEmpty && (
                  <RecentItems
                    onHover={handleHover}
                    onSelect={handleSelect}
                  />
                )}

                {!isEmpty && searchTerm.length < 2 && (
                  <div className="py-6 text-center text-sm">
                    <p className="text-muted-foreground">
                      Type at least 2 characters to search
                    </p>
                  </div>
                )}

                {!isEmpty && searchTerm.length >= 2 && (
                  <SearchResults
                    loading={loading}
                    onHover={handleHover}
                    onSelect={handleSelect}
                    opportunities={results.opportunities}
                  />
                )}
              </CommandList>
            </Command>

            {/* Action hints - Always at bottom */}
            <div className="text-muted-foreground bg-muted/30 shrink-0 border-t px-4 py-2 text-xs">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
                    ↑↓
                  </kbd>
                  <span>navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
                    Esc
                  </kbd>
                  <span>close</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right panel - Preview */}
          <ItemPreview
            item={selectedItem}
            onAction={handleAction}
          />
        </div>
      </CommandDialog>

      {dealSelectsData && dealAccountsData && (
        <NewDealDialog
          data3={dealAccountsData}
          dataEmployeesAndDealSelects={dealSelectsData}
          hideTrigger
          onOpenChange={setIsCreateDealOpen}
          open={isCreateDealOpen}
        />
      )}
    </>
  );
}
